import { useMemo, useState } from 'react';

import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FilterEmptyState } from '@/components/ui/custom/FilterEmptyState';
import { useBrainUniqueTags } from '@/hooks/useBrainUniqueTags';
import { TagsMode } from '@/shared/types/analytics';

import { FilterSearchField } from './FilterSearchField';
import { TagsModeSelect } from './TagsModeSelect';
import { VirtualizedTagList } from './VirtualizedTagList';

interface TagsFilterProps {
  filterIndex: number;
}

/**
 * Renders a searchable list of available tags with checkboxes for filtering
 */
export const TagsFilter = ({ filterIndex }: TagsFilterProps) => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext();
  const { update } = useFieldArray({ control, name: 'filters' });
  const [searchText, setSearchText] = useState('');

  const watchedTags = useWatch({ control, name: `filters.${filterIndex}.tags` }) as
    string[] | undefined;
  const watchedMode = useWatch({ control, name: `filters.${filterIndex}.tagsMode` }) as
    TagsMode | undefined;
  const currentTags = useMemo<string[]>(
    () => (Array.isArray(watchedTags) ? watchedTags : []),
    [watchedTags]
  );

  const { availableTags, isLoading, error } = useBrainUniqueTags({ currentTags, searchText });

  const handleTagToggle = (tag: string, checked: boolean) => {
    const existing = Array.isArray(currentTags) ? [...currentTags] : [];
    const next = checked ? [...existing, tag] : existing.filter((t: string) => t !== tag);

    const currentFilter = getValues(`filters.${filterIndex}`);
    update(filterIndex, { ...currentFilter, tags: next });
  };

  const handleModeChange = (tagsMode: TagsMode) => {
    const currentFilter = getValues(`filters.${filterIndex}`);
    update(filterIndex, { ...currentFilter, tagsMode });
  };

  if (isLoading) {
    return (
      <div className="p-4 text-sm text-foreground-muted">{t('analytics.filters.loading_tags')}</div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-destructive">
        {t('analytics.filters.failed_to_load_tags')}
      </div>
    );
  }

  if (availableTags.length === 0 && !searchText) {
    return <FilterEmptyState text={t('analytics.filters.no_tags_available')} />;
  }

  return (
    <div className="space-y-3">
      <p className="px-2 text-xs text-foreground-muted">
        {t('analytics.filters.tags_time_scoped_hint')}
      </p>
      {currentTags.length >= 2 && (
        <TagsModeSelect value={watchedMode} onChange={handleModeChange} />
      )}
      <FilterSearchField
        value={searchText}
        onChange={setSearchText}
        placeholder={t('analytics.filters.search_tags')}
      />

      {availableTags.length === 0 && searchText ? (
        <FilterEmptyState text={t('analytics.filters.no_tags_match')} />
      ) : (
        <VirtualizedTagList
          tags={availableTags}
          selectedTags={currentTags}
          idPrefix="tag"
          onToggle={handleTagToggle}
          scrollResetKey={searchText}
        />
      )}
    </div>
  );
};
